import { useState, type ReactNode, type RefObject } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

interface ConfirmDialogProps {
  open: boolean
  title: string
  summary: ReactNode
  confirmLabel: string
  tone?: 'primary' | 'danger'
  /** Ask for a reason; confirm stays disabled until it has `minLength` non-space characters. */
  reason?: { label: string; minLength?: number }
  pending?: boolean
  error?: string | null
  returnFocusRef?: RefObject<HTMLElement | null>
  onConfirm: (reason: string) => void
  onCancel: () => void
}

// The dialog stays open and shows `error` when the request fails; the caller closes it on success.
export default function ConfirmDialog(props: ConfirmDialogProps) {
  const { open, title, summary, confirmLabel, tone = 'primary', reason, pending, error, returnFocusRef, onConfirm, onCancel } = props
  return (
    <Dialog open={open} onClose={onCancel} label={title} returnFocusRef={returnFocusRef}>
      <ConfirmBody
        title={title}
        summary={summary}
        confirmLabel={confirmLabel}
        tone={tone}
        reason={reason}
        pending={pending}
        error={error}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </Dialog>
  )
}

// Mounted only while open, so the reason text resets on every open.
function ConfirmBody({
  title,
  summary,
  confirmLabel,
  tone,
  reason,
  pending,
  error,
  onConfirm,
  onCancel,
}: Omit<ConfirmDialogProps, 'open' | 'returnFocusRef'>) {
  const [text, setText] = useState('')
  const tooShort = reason ? text.trim().length < (reason.minLength ?? 1) : false
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!pending && !tooShort) onConfirm(text.trim())
      }}
    >
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="text-sm text-muted-foreground">{summary}</div>
      {reason ? (
        <Field label={reason.label}>
          {/* data-autofocus: React polyfills autoFocus before the <dialog> is open, so Dialog re-focuses it itself. */}
          <Input value={text} onChange={(e) => setText(e.target.value)} autoFocus data-autofocus="" />
        </Field>
      ) : null}
      {/* Solid tone box: red text straight on glass drops under 4.5:1 over dense rows. */}
      {error ? (
        <p role="alert" className="rounded-md bg-[var(--tone-danger-bg)] px-3 py-2 text-sm font-semibold text-[var(--tone-danger-fg)]">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" variant={tone === 'danger' ? 'destructiveSolid' : 'default'} disabled={pending || tooShort}>
          {pending ? 'Working…' : confirmLabel}
        </Button>
      </div>
    </form>
  )
}
